/**
 * AccountantsTable: list layout with working search filter (pill-shaped).
 *
 * Search filter runs locally against name + email + firm (case-insensitive).
 * Empty-match state shows a plain line below the table header.
 */

import { useId, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Plus } from "lucide-react";
import type { MyAccountant } from "../myAccountantTypes";
import { AccountantRow } from "./AccountantRow";
import { PALETTE } from "../constants";

interface AccountantsTableProps {
  accountants: MyAccountant[];
  onResend: (id: string) => void;
  onDelete: (id: string) => void;
  onViewAccess: (accountant: MyAccountant) => void;
}

export function AccountantsTable({
  accountants,
  onResend,
  onDelete,
  onViewAccess,
}: AccountantsTableProps) {
  const uid = useId().replace(/:/g, "");
  const styleTag = useAccountantsTableStyles(uid);
  const navigate = useNavigate();
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return accountants;
    return accountants.filter((a) => {
      const name = (a.name || "").toLowerCase();
      const email = (a.email || "").toLowerCase();
      const firm = (a.firmName || "").toLowerCase();
      return name.includes(q) || email.includes(q) || firm.includes(q);
    });
  }, [accountants, query]);

  const goInvite = () => navigate("/accounting/my-accountant/invite");

  return (
    <>
      {styleTag}
      <section className={`at-${uid}-wrap`}>
        <header className={`at-${uid}-head`}>
          <div>
            <p className={`at-${uid}-eyebrow`}>My accountant</p>
            <h2 className={`at-${uid}-title`}>Your accountants</h2>
            <p className={`at-${uid}-count`}>
              {accountants.length} accountant
              {accountants.length === 1 ? "" : "s"} with access to your books
            </p>
          </div>
          <button
            type="button"
            className={`at-${uid}-invite`}
            onClick={goInvite}
          >
            <Plus size={14} strokeWidth={2.2} />
            Invite accountant
          </button>
        </header>

        <div className={`at-${uid}-search-row`}>
          <label className={`at-${uid}-search-label`} htmlFor={`at-${uid}-q`}>
            Search
          </label>
          <div className={`at-${uid}-search`}>
            <span className={`at-${uid}-search-ico`} aria-hidden="true">
              <Search size={14} strokeWidth={2} />
            </span>
            <input
              id={`at-${uid}-q`}
              type="text"
              className={`at-${uid}-search-input`}
              placeholder="Search by name, email, or firm"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
        </div>

        <div className={`at-${uid}-table`}>
          <div className={`at-${uid}-row at-${uid}-head-row`}>
            <div>Name</div>
            <div>Email</div>
            <div>Firm</div>
            <div>Status</div>
            <div>Date added</div>
            <div className={`at-${uid}-head-action`}>Action</div>
          </div>
          {filtered.map((a) => (
            <AccountantRow
              key={a.id}
              accountant={a}
              onResend={onResend}
              onDelete={onDelete}
              onViewAccess={onViewAccess}
            />
          ))}
          {filtered.length === 0 && query.trim() && (
            <div className={`at-${uid}-empty`}>
              No accountants match &ldquo;{query}&rdquo;.
            </div>
          )}
        </div>
      </section>
    </>
  );
}

function useAccountantsTableStyles(uid: string) {
  const css = `
    .at-${uid}-wrap { width: 100%; }
    .at-${uid}-head {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 20px;
      margin-bottom: 22px;
      flex-wrap: wrap;
    }
    .at-${uid}-eyebrow {
      font-size: 11px;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      color: ${PALETTE.teal};
      font-weight: 700;
      margin: 0 0 8px;
    }
    .at-${uid}-title {
      font-size: 24px;
      font-weight: 700;
      color: ${PALETTE.ink};
      margin: 0 0 6px;
      letter-spacing: -0.015em;
    }
    .at-${uid}-count {
      font-size: 13px;
      color: ${PALETTE.ink2};
      margin: 0;
      font-weight: 500;
    }
    .at-${uid}-invite {
      background: ${PALETTE.ink};
      color: #ffffff;
      border: 0;
      padding: 10px 18px;
      border-radius: 9px;
      font-size: 13.5px;
      font-weight: 600;
      cursor: pointer;
      font-family: inherit;
      display: inline-flex;
      align-items: center;
      gap: 7px;
      box-shadow: 0 2px 6px -1px rgba(14, 26, 26, 0.18);
    }
    .at-${uid}-invite:hover {
      transform: translateY(-1px);
      box-shadow: 0 6px 14px -2px rgba(14, 26, 26, 0.25);
    }

    .at-${uid}-search-row { margin-bottom: 14px; }
    .at-${uid}-search-label {
      font-size: 12.5px;
      color: ${PALETTE.ink};
      font-weight: 600;
      margin: 0 0 6px;
      display: block;
    }
    .at-${uid}-search {
      background: #ffffff;
      border: 1.5px solid ${PALETTE.ink4};
      border-radius: 999px;
      padding: 0 16px 0 40px;
      max-width: 420px;
      position: relative;
      display: flex;
      align-items: center;
      transition: border-color 160ms;
    }
    .at-${uid}-search:focus-within { border-color: ${PALETTE.ink}; }
    .at-${uid}-search-ico {
      position: absolute;
      left: 14px;
      top: 50%;
      transform: translateY(-50%);
      color: ${PALETTE.ink};
      display: inline-flex;
    }
    .at-${uid}-search-input {
      width: 100%;
      border: 0;
      outline: 0;
      padding: 9px 0;
      font-size: 13px;
      color: ${PALETTE.ink};
      background: transparent;
      font-family: inherit;
    }
    .at-${uid}-search-input::placeholder { color: ${PALETTE.ink2}; }

    .at-${uid}-table {
      background: #ffffff;
      border: 1px solid ${PALETTE.line};
      border-radius: 10px;
      overflow: visible;
    }
    .at-${uid}-row {
      display: grid;
      grid-template-columns: 1.3fr 2fr 1.4fr 1fr 1fr auto;
      gap: 18px;
      padding: 14px 20px;
      align-items: center;
      font-size: 13px;
      color: ${PALETTE.ink};
    }
    .at-${uid}-head-row {
      background: ${PALETTE.page};
      border-bottom: 1.5px solid ${PALETTE.ink4};
      font-size: 11px;
      letter-spacing: 0.09em;
      text-transform: uppercase;
      font-weight: 700;
      color: ${PALETTE.ink};
      padding-top: 14px;
      padding-bottom: 12px;
      border-top-left-radius: 10px;
      border-top-right-radius: 10px;
    }
    .at-${uid}-head-action { text-align: right; min-width: 160px; }

    .at-${uid}-empty {
      padding: 24px 20px;
      font-size: 13px;
      color: ${PALETTE.ink2};
      text-align: center;
      border-top: 1px solid ${PALETTE.line};
    }

    @media (max-width: 720px) {
      .at-${uid}-head { flex-direction: column; align-items: stretch; }
      .at-${uid}-invite { align-self: flex-end; }
      .at-${uid}-search { max-width: 100%; }
      .at-${uid}-head-row { display: none; }
      .at-${uid}-row { grid-template-columns: 1fr; gap: 6px; }
    }
  `;
  return <style>{css}</style>;
}